import React, { useState, useEffect } from "react"
import Button from "@mui/material/Button"
import AddCircleOutlineRoundedIcon from "@mui/icons-material/AddCircleOutlineRounded"
import PageHeading from "components/common/LayoutComponents/PageHeading"
import { verifyResponse } from "utils/httpRequest"
import { PERMISSIONS } from "constants/permission"
import { UsePermissionVerification } from "components/customHooks"
import LoadingScreen from "components/common/LoadingScreen"
import Tooltip from "@mui/material/Tooltip"
import IconButton from "@mui/material/IconButton"
import DeleteForeverIcon from "@mui/icons-material/DeleteForever"
import EditIcon from "@mui/icons-material/Edit"
import Chip from "@mui/material/Chip"
import Alert from "@mui/material/Alert"
import { calculateNewRank } from "utils/data"
import { Container, Draggable } from "react-smooth-dnd"
import StartHereDlg from "./StartHereDlg"
import { UseConfirmDialog } from "components/customHooks"
import { START_RESOURCE_DATA_TYPE } from "constants/global"
import {
  listStartResource,
  deleteStartResource,
  exchangeStartResourceIndex,
} from "services/startHere"

import { StartHereWrapper } from "./StartHere.style"

const StartHere = () => {
  const [loading, setLoading] = useState(false)
  const [startResources, setStartResources] = useState([])

  const AccessControl = UsePermissionVerification()

  const [ConfirmDialog, openConfirmDialog] = UseConfirmDialog({
    title: "Delete Confirm",
    description: "Are you sure want delete this ?",
    agreeText: "Yes",
    disagreeText: "No",
  })

  const [openStartHereDlg, startHereDlg, loadingStartHereDlg] = StartHereDlg({
    onSubmit: () => {
      queryStartInnercise()
    },
  })

  const removeStartInnercise = async (removeResourceId) => {
    setLoading(true)
    const response = await deleteStartResource({ id: removeResourceId })
    setLoading(false)
    verifyResponse(response)
    queryStartInnercise()
  }

  const queryStartInnercise = async () => {
    setLoading(true)
    const response = await listStartResource({
      pageNo: 1,
      pageSize: 1000,
      dataType: START_RESOURCE_DATA_TYPE.INNERCISE,
      dataTitle: "",
    })
    setLoading(false)
    verifyResponse(response)
    setStartResources(response.data.list)
  }

  const StartResourceCard = ({ resource }) => {
    return (
      <div className="resource-item">
        <div className="thumb-container">
          {resource.isHome === 1 && (
            <Chip className="chip" label="Home" size="small" color="success" />
          )}
          {resource.tierAccess ? (
            <Chip
              className="chip"
              label={`Tier: ${resource.tierAccess}${resource.tierExclusive ? ' (exclusive)' : ''}`}
              size="small"
              color="primary"
              sx={{ ml: 1 }}
            />
          ) : null}
          <img
            className="thumb"
            src={
              resource.innerciseDto.thumbResourceDto.urlDto.imgUrl ||
              resource.innerciseDto.thumbResourceDto.urlDto.originalUrl ||
              ""
            }
            alt="thumb"
          />
        </div>
        <div className="content">
          <div className="title">
            ({resource.innerciseDto.id}) {resource.innerciseDto.title}
          </div>
          <p>{resource.innerciseDto.description}</p>
          <div className="actions">
            {AccessControl.Access.deleteStartResource && (
              <Tooltip title="Delete Resource">
                <IconButton
                  sx={{ color: "error.main" }}
                  aria-label="edit resource"
                  component="label"
                  onClick={() => {
                    openConfirmDialog(async () => {
                      removeStartInnercise(resource.id)
                    })
                  }}
                >
                  <DeleteForeverIcon />
                </IconButton>
              </Tooltip>
            )}
            {AccessControl.Access.editStartResource && (
              <Tooltip title="Edit Resource">
                <IconButton
                  sx={{ color: "primary.main" }}
                  aria-label="edit resource"
                  component="label"
                  onClick={() => {
                    openStartHereDlg(resource)
                  }}
                >
                  <EditIcon />
                </IconButton>
              </Tooltip>
            )}
          </div>
        </div>
      </div>
    )
  }

  useEffect(() => {
    queryStartInnercise()
  }, [])

  useEffect(() => {
    AccessControl.SetAccess({
      ...AccessControl.Access,
      addStartResource: AccessControl.VerifyAccess([
        PERMISSIONS.START_HERE.START_HERE_RESOURCE_ADD,
      ]),
      deleteStartResource: AccessControl.VerifyAccess([
        PERMISSIONS.START_HERE.START_HERE_RESOURCE_DELETE,
      ]),
      editStartResource: AccessControl.VerifyAccess([
        PERMISSIONS.START_HERE.START_HERE_RESOURCE_EDIT,
      ]),
      exchangeIndexStartResource: AccessControl.VerifyAccess([
        PERMISSIONS.START_HERE.START_HERE_RESOURCE_EXCHANGE_INDEX,
      ]),
      listStartResource: AccessControl.VerifyAccess([
        PERMISSIONS.START_HERE.START_HERE_RESOURCE_LIST,
      ]),
    })
  }, [AccessControl.UserPermissions])

  return (
    <StartHereWrapper>
      <PageHeading>
        <div className="heading-copy">
          <h1 className="page-title">Start Here Resources</h1>
          <p className="page-subtitle">
            Curate onboarding innercises and prioritize what members see first.
          </p>
        </div>
        <div className="actions">
          {AccessControl.Access.addStartResource && (
            <Button
              className="new-resource-btn"
              variant="contained"
              size="medium"
              startIcon={<AddCircleOutlineRoundedIcon />}
              onClick={() => {
                openStartHereDlg()
              }}
            >
              Add Innercise
            </Button>
          )}
        </div>
      </PageHeading>
      <div className="page-surface">
        <Alert className="priority-alert" severity="info">
          <div style={{ fontWeight: 700, marginBottom: 6 }}>
            Start Here ordering and priority
          </div>
          <div>
            The app automatically orders the Start Here carousel by priority:
            <ol style={{ margin: "6px 0 0 18px" }}>
              <li>
                <b>Product-gated</b> items (set <code>Tier Access</code> to{" "}
                <code>product</code>) are shown first for members who own that
                product
              </li>
              <li>
                <b>All other items</b> (tier-gated or app-wide) are shown next
                in the exact drag-and-drop order configured on this page
              </li>
            </ol>
          </div>
        </Alert>
        <div>
          {startResources.length > 0 && (
            <Container
              onDrop={async (e) => {
                const updateIndexArray = calculateNewRank({
                  addedIndex: e.addedIndex,
                  removedIndex: e.removedIndex,
                  indexString: "dataIndex",
                  list: startResources,
                  updateOriginalList: setStartResources,
                })
                await exchangeStartResourceIndex(updateIndexArray)
                queryStartInnercise()
              }}
            >
              {startResources.map((resource) => {
                return AccessControl.Access.exchangeIndexStartResource ? (
                  <Draggable key={resource.id}>
                    <StartResourceCard resource={resource} />
                  </Draggable>
                ) : (
                  <StartResourceCard key={resource.id} resource={resource} />
                )
              })}
            </Container>
          )}
          {startResources.length === 0 && (
            <div className="empty-state">No Start Here resources found.</div>
          )}
        </div>
      </div>
      <LoadingScreen loading={loading || loadingStartHereDlg} />
      {startHereDlg}
      {ConfirmDialog}
    </StartHereWrapper>
  )
}

export default StartHere
